import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "@lift-admin/utils/debounce";
import apiClient from "@lift-admin/utils/apiClient";

export type GrapevineSlugStatus =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "error";

const SLUG_CHECK_DELAY_MS = 300;

export function grapevineSlugSubmitError(
  isPerformanceCreator: boolean,
  slug: string,
  status: GrapevineSlugStatus,
): string | null {
  if (!isPerformanceCreator || !slug.trim()) return null;
  if (status === "checking") {
    return "Still checking whether the Grapevine slug is available";
  }
  return null;
}

function availabilityStatus(payload: { available?: unknown } | null | undefined): GrapevineSlugStatus {
  if (payload?.available === true) return "available";
  if (payload?.available === false) return "taken";
  return "error";
}

export function useGrapevineSlugAvailability(
  slug: string,
  excludeLiftBrandId?: number | string | null,
): { status: GrapevineSlugStatus; resolvedSlug: string | null } {
  const [status, setStatus] = useState<GrapevineSlugStatus>("idle");
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const runCheck = useMemo(
    () =>
      debounce((value: string, exclude: string | undefined, requestId: number) => {
        const params = new URLSearchParams({ slug: value });
        if (exclude) params.set("exclude_lift_brand_id", exclude);

        apiClient
          .get(`/brand/v1/admin/grapevine/slug-availability?${params.toString()}`)
          .then((response) => {
            if (requestIdRef.current !== requestId) return;
            const payload = response.data?.response ?? response.data;
            const resolved =
              typeof payload?.resolved_slug === "string" ? payload.resolved_slug : null;
            setStatus(availabilityStatus(payload));
            setResolvedSlug(resolved);
          })
          .catch(() => {
            if (requestIdRef.current !== requestId) return;
            setStatus("error");
            setResolvedSlug(null);
          });
      }, SLUG_CHECK_DELAY_MS),
    [],
  );

  useEffect(() => {
    const trimmed = slug.trim();
    const requestId = ++requestIdRef.current;
    if (!trimmed) {
      setStatus("idle");
      setResolvedSlug(null);
      return;
    }

    setStatus("checking");
    setResolvedSlug(null);
    const exclude =
      excludeLiftBrandId === undefined ||
      excludeLiftBrandId === null ||
      String(excludeLiftBrandId).trim() === ""
        ? undefined
        : String(excludeLiftBrandId).trim();
    runCheck(trimmed, exclude, requestId);
  }, [slug, excludeLiftBrandId, runCheck]);

  return { status, resolvedSlug };
}

export function GrapevineSlugAvailabilityMessage({
  status,
  slug,
  resolvedSlug,
}: {
  status: GrapevineSlugStatus;
  slug?: string;
  resolvedSlug?: string | null;
}) {
  if (status === "checking") {
    return <p className="text-xs text-muted-foreground">Checking…</p>;
  }
  if (status === "available") {
    return <p className="text-xs text-green-600">Slug is available</p>;
  }
  if (status === "taken") {
    const resolved = resolvedSlug?.trim();
    if (!resolved || resolved === slug?.trim()) return null;
    return (
      <p className="text-xs text-muted-foreground">Slug will be saved as {resolved}</p>
    );
  }
  if (status === "error") {
    return (
      <p className="text-xs text-amber-600">
        Unable to check slug availability. You can still save.
      </p>
    );
  }
  return null;
}
